import type { BlockModel, Workspace } from '@blocksuite/store';

/**
 * How a block whose trash the server will do (table, automation) shows until
 * the server's receipt syncs: gone for now (`hidden`), or in place with its
 * phase (`visible`). A local signal only; nothing of it is written to the doc.
 */
export type BlockPendingDeletionState =
  | { kind: 'none' }
  | { kind: 'hidden' }
  | { kind: 'visible'; phase: 'queued' | 'confirming' | 'unconfirmed' };

/** Host half: the queued trash of the blocks of one workspace. */
export interface BlockPendingDeletionSource {
  state(docId: string, blockId: string): BlockPendingDeletionState;
  subscribe(onChange: () => void): () => void;
  /** True when the queued trash will never be sent. */
  cancel(docId: string, blockId: string): Promise<boolean>;
  /** Sends an unconfirmed trash again. */
  retry(docId: string, blockId: string): Promise<void>;
}

const NONE: BlockPendingDeletionState = { kind: 'none' };

/**
 * The pending deletion of one workspace: its source, if the host registered
 * one, and the blocks that follow it. Blocks follow the registry, not the
 * source, so a block connected before the host registers still hears of it.
 */
type Registry = {
  source: BlockPendingDeletionSource | null;
  stopSource: (() => void) | null;
  listeners: Set<() => void>;
};

const registries = new WeakMap<Workspace, Registry>();

function registryOf(workspace: Workspace): Registry {
  let registry = registries.get(workspace);
  if (!registry) {
    registry = { source: null, stopSource: null, listeners: new Set() };
    registries.set(workspace, registry);
  }
  return registry;
}

function notify(registry: Registry): void {
  for (const listener of [...registry.listeners]) listener();
}

/**
 * Register (or clear with `null`) the source of a workspace; the blocks of
 * the workspace are told either way.
 */
export function setBlockPendingDeletionSource(
  workspace: Workspace,
  source: BlockPendingDeletionSource | null
): void {
  const registry = registryOf(workspace);
  registry.stopSource?.();
  registry.source = source;
  registry.stopSource = source?.subscribe(() => notify(registry)) ?? null;
  notify(registry);
}

/** The source of the model's workspace, if the host registered one. */
export function blockPendingDeletionSource(
  model: BlockModel
): BlockPendingDeletionSource | undefined {
  return registries.get(model.store.workspace)?.source ?? undefined;
}

/**
 * The pending state of a block. Canvas hit tests read it, so it is a lookup
 * without allocation.
 */
export function blockPendingDeletionState(
  model: BlockModel
): BlockPendingDeletionState {
  return (
    blockPendingDeletionSource(model)?.state(model.store.id, model.id) ?? NONE
  );
}

/**
 * Calls back when any pending state of the model's workspace changes, also
 * when its source is registered or cleared later.
 */
export function subscribeBlockPendingDeletion(
  model: BlockModel,
  onChange: () => void
): () => void {
  const registry = registryOf(model.store.workspace);
  registry.listeners.add(onChange);
  return () => {
    registry.listeners.delete(onChange);
  };
}
